import React from "react";
import "./TopicSelector.less";

interface Topic {
  id: string;
  label: string;
  icon: string;
  question: string;
  template: string;
}

interface TopicSelectorProps {
  topics: readonly Topic[];
  onTopicSelect: (topicId: string) => void;
  onBack: () => void;
  onSkip: () => void;
  backToPhotoText?: string;
  skipButtonText?: string;
  selectedTopic?: string;
  hideButtons?: boolean;
  isDesktop?: boolean;
  isWideDesktop?: boolean;
}

/**
 * Grid of selectable conversation topics for the questionnaire
 * - Displays a visual menu of topics with icons for the user to choose from
 * - Adapts layout for desktop/mobile views with different sizing and spacing
 * - Can optionally hide navigation buttons when used as a sub-component in desktop view
 */
const TopicSelector: React.FC<TopicSelectorProps> = ({
  topics,
  onTopicSelect,
  onBack,
  onSkip,
  backToPhotoText = "Back to photo",
  skipButtonText = "Skip message",
  selectedTopic = "",
  hideButtons = false,
  isDesktop = false,
  isWideDesktop = false
}) => {
  return (
    <div
      className={`topic-selector-container ${isDesktop ? "desktop" : ""} ${isWideDesktop ? "wide-desktop" : ""}`}
      role="region"
      aria-label="Topic Selection"
    >
      <div className="topic-selector-grid" role="list">
        {topics.map((topic) => (
          <button
            key={topic.id}
            onClick={() => onTopicSelect(topic.id)}
            className={`topic-selector-button __staple--small ${selectedTopic === topic.id ? "selected" : ""}`}
            aria-label={`Select ${topic.label} topic`}
            role="listitem"
            aria-selected={selectedTopic === topic.id}
          >
            <img src={topic.icon} alt="" aria-hidden="true" className="topic-selector-icon" />
            <span>{topic.label}</span>
          </button>
        ))}
      </div>

      {!hideButtons && (
        <nav className="topic-selector-navigation" aria-label="Topic navigation">
          <button onClick={onBack} className="btn-link" aria-label="Go back to photo">
            <span className="arrow-glyph">←</span> {backToPhotoText}
          </button>
          <button onClick={onSkip} className="btn-link" aria-label="Skip message section">
            {skipButtonText}
          </button>
        </nav>
      )}
    </div>
  );
};

export default TopicSelector;
